import config from "../Configurations";
import { apiRequest } from "util/services";
import { localDay } from "util/advisorPopup";

/**
 * GET dashboards/cx_gpt/settings — user LLM preferences (no raw API key).
 * @returns {Promise<object>}
 */
export const fetchCxGptSettings = () =>
  apiRequest("dashboards/cx_gpt/settings", "GET", null, true);

export const fetchCxGptProviders = () =>
  apiRequest("dashboards/cx_gpt/providers", "GET", null, true);

export const saveCxGptProvider = (provider, body) =>
  apiRequest(`dashboards/cx_gpt/providers/${provider}`, "PUT", body, true);

export const deleteCxGptProvider = (provider) =>
  apiRequest(`dashboards/cx_gpt/providers/${provider}`, "DELETE", null, true);

export const fetchCxGptProviderModels = (provider) =>
  apiRequest(`dashboards/cx_gpt/providers/${provider}/models`, "GET", null, true);

export const saveCxGptProviderSelection = (provider, model) =>
  apiRequest(
    `dashboards/cx_gpt/providers/${provider}/selection`,
    "PUT",
    { model },
    true
  );

/**
 * GET dashboards/cx_gpt/models — model ids for dropdown (local or BYOK).
 * @returns {Promise<object>}
 */
export const fetchCxGptModels = () =>
  apiRequest("dashboards/cx_gpt/models", "GET", null, true);

/**
 * GET dashboards/cx_gpt/catalog — DB gpt_models rows for active_gpt_model_id (relational only).
 * @returns {Promise<{ catalog_available?: boolean, models?: Array }>}
 */
export const fetchCxGptCatalog = () =>
  apiRequest("dashboards/cx_gpt/catalog", "GET", null, true);

/**
 * POST dashboards/categories — org industry from Industries via EnterpriseCustomers.category_id.
 * @returns {Promise<{ name: string } | null>}
 */
export const fetchOrgIndustry = async () => {
  const result = await apiRequest("dashboards/categories", "POST", null, true);
  if (!result || result.cancel || result.error) {
    return null;
  }
  const name = result?.orgIndustry?.name;
  if (typeof name !== "string") {
    return null;
  }
  const trimmed = name.trim();
  if (!trimmed || trimmed === "Others") {
    return null;
  }
  return { name: trimmed };
};

/**
 * PUT dashboards/cx_gpt/settings — save mode / BYOK (api_key omitted = unchanged, "" = clear).
 * @param {object} body — { llm_source, byok_provider?, byok_base_url?, byok_model?, api_key?, active_gpt_model_id? }
 */
export const saveCxGptSettings = (body) =>
  apiRequest("dashboards/cx_gpt/settings", "PUT", body, true);

/**
 * DELETE dashboards/cx_gpt/settings — reset to defaults.
 */
export const deleteCxGptSettings = () =>
  apiRequest("dashboards/cx_gpt/settings", "DELETE", null, true);

/**
 * POST dashboards/gpt_answer — same contract as ChatBot (single turn).
 * @param {string} text
 * @returns {Promise<{ success?: string, error?: boolean }>}
 */
export const requestGptAnswer = (text) =>
  apiRequest("dashboards/gpt_answer", "POST", { text });

export const normalizeThoughtPreview = (text) => {
  if (!text) return "";
  return String(text)
    .replace(/\*\*/g, "")
    .replace(/[#*_`]/g, "")
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

/**
 * POST dashboards/cx_gpt/chat — streaming SSE with Gemini thinking tokens.
 *
 * @param {Array<{ role: 'user' | 'assistant', content: string }>} messages
 * @param {object} options — same fields as requestCxGptChat
 * @param {{ onThought?: (delta: string, accumulated: string) => void, onContent?: (delta: string, accumulated: string, replace?: boolean) => void, onStatus?: (detail: string) => void, onRetry?: (event: object) => void, onDashboardPicker?: (picker: object) => void, onDone?: (payload: object) => void, onError?: (message: string) => void }} callbacks
 * @returns {Promise<{ content?: string, suggested_followups?: string[], guidance?: string, dashboard_picker?: object, error?: string, cancel?: boolean }>}
 */
const invokeStreamCallback = (fn, ...args) => {
  if (typeof fn === "function") {
    fn(...args);
  }
};

export const streamCxGptChat = async (messages, options = {}, callbacks = {}) => {
  const body = { messages, stream: true };
  const context = options.context && typeof options.context === "object" ? options.context : {};
  body.context = { ...context, localDate: localDay() };
  if (options.model != null && String(options.model).trim()) {
    body.model = String(options.model).trim();
  }
  if (options.provider != null && String(options.provider).trim()) {
    body.provider = String(options.provider).trim().toLowerCase();
  }
  if (options.sessionId != null && String(options.sessionId).trim()) {
    body.session_id = String(options.sessionId).trim();
  }
  if (options.contextUsedTokens != null) {
    body.context_used_tokens = Number(options.contextUsedTokens);
  }
  if (options.contextMaxTokens != null) {
    body.context_max_tokens = Number(options.contextMaxTokens);
  }
  if (options.lastRoute != null && String(options.lastRoute).trim()) {
    body.last_route = String(options.lastRoute).trim();
  }

  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");
  myHeaders.append("Accept", "text/event-stream");

  const tokenStr = window.localStorage.getItem("jwt_token");
  if (tokenStr) {
    try {
      const item = JSON.parse(tokenStr);
      const now = new Date();
      if (now.getTime() <= item.expiry && item.value) {
        myHeaders.append("Authorization", "Bearer " + item.value);
      }
    } catch (_) {}
  }

  let response;
  try {
    response = await fetch(config.BASE_URL + "dashboards/cx_gpt/chat", {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify(body),
    });
  } catch (_) {
    return { cancel: true };
  }

  if (response.status === 401) {
    return { error: "Unauthorized", cancel: true };
  }

  const contentType = response.headers.get("content-type") || "";
  if (response.ok && contentType.includes("application/json")) {
    try {
      const data = await response.json();
      if (data && data.error) {
        return { error: String(data.error) };
      }
      if (data && data.success != null) {
        const content = String(data.success);
        const picker =
          data.dashboard_picker &&
          typeof data.dashboard_picker === "object" &&
          Array.isArray(data.dashboard_picker.dashboards) &&
          data.dashboard_picker.dashboards.length
            ? data.dashboard_picker
            : undefined;
        const periodPicker =
          data.period_picker &&
          typeof data.period_picker === "object" &&
          Array.isArray(data.period_picker.periods) &&
          data.period_picker.periods.length
            ? data.period_picker
            : undefined;
        const payload = {
          content,
          pivony_suggested_followups: data.suggested_followups,
          pivony_guidance: data.guidance,
          pivony_dashboard_picker: picker,
          pivony_period_picker: periodPicker,
        };
        if (picker) {
          invokeStreamCallback(callbacks.onDashboardPicker, picker);
        }
        if (periodPicker) {
          invokeStreamCallback(callbacks.onPeriodPicker, periodPicker);
        }
        invokeStreamCallback(callbacks.onDone, payload);
        return {
          content,
          suggested_followups: Array.isArray(data.suggested_followups)
            ? data.suggested_followups
            : [],
          guidance: typeof data.guidance === "string" ? data.guidance : "",
          dashboard_picker: picker,
          period_picker: periodPicker,
        };
      }
    } catch (_) {}
  }

  if (!response.ok || !contentType.includes("text/event-stream")) {
    try {
      const errBody = await response.json();
      if (errBody && errBody.error) {
        return { error: String(errBody.error) };
      }
    } catch (_) {}
    return { error: "LLM service returned an error" };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let thoughtAcc = "";
  let contentAcc = "";
  let dashboardPickerAcc = null;
  let periodPickerAcc = null;
  let kpiMetricPickerAcc = null;
  let kpiTeamPickerAcc = null;
  let chartsAcc = [];
  let donePayload = null;
  let streamError = null;

  const parseEvent = (line) => {
    const trimmed = line.trim();
    if (!trimmed.startsWith("data:")) return;
    const dataStr = trimmed.slice(5).trim();
    if (!dataStr || dataStr === "[DONE]") return;
    let event;
    try {
      event = JSON.parse(dataStr);
    } catch (_) {
      return;
    }
    if (!event || typeof event !== "object") return;

    if (event.type === "thought" && typeof event.delta === "string") {
      thoughtAcc += event.delta;
      invokeStreamCallback(callbacks.onThought, event.delta, thoughtAcc);
    } else if (event.type === "status" && event.phase === "retry") {
      invokeStreamCallback(callbacks.onRetry, event);
    } else if (
      event.type === "status" &&
      (event.phase === "thinking" ||
        (event.suppress_content_bubble && event.phase !== "tool"))
    ) {
      invokeStreamCallback(callbacks.onThinkingStatus, event);
    } else if (
      event.type === "status" &&
      event.phase === "tool" &&
      typeof event.detail === "string"
    ) {
      invokeStreamCallback(callbacks.onStatus, event.detail);
    } else if (event.type === "content" && typeof event.delta === "string") {
      const isReplace = Boolean(event.replace);
      if (!isReplace && !event.delta.trim()) {
        return;
      }
      if (isReplace) {
        contentAcc = event.delta;
      } else {
        contentAcc += event.delta;
      }
      if (!contentAcc.trim() && !isReplace) {
        return;
      }
      invokeStreamCallback(
        callbacks.onContent,
        event.delta,
        contentAcc,
        isReplace,
      );
    } else if (
      event.type === "dashboard_picker" &&
      event.picker &&
      typeof event.picker === "object" &&
      Array.isArray(event.picker.dashboards) &&
      event.picker.dashboards.length
    ) {
      dashboardPickerAcc = event.picker;
      invokeStreamCallback(callbacks.onDashboardPicker, event.picker);
    } else if (
      event.type === "period_picker" &&
      event.picker &&
      typeof event.picker === "object" &&
      Array.isArray(event.picker.periods) &&
      event.picker.periods.length
    ) {
      periodPickerAcc = event.picker;
      invokeStreamCallback(callbacks.onPeriodPicker, event.picker);
    } else if (
      event.type === "kpi_metric_picker" &&
      event.picker &&
      typeof event.picker === "object" &&
      Array.isArray(event.picker.metrics) &&
      event.picker.metrics.length
    ) {
      kpiMetricPickerAcc = event.picker;
      invokeStreamCallback(callbacks.onKpiMetricPicker, event.picker);
    } else if (
      event.type === "kpi_team_picker" &&
      event.picker &&
      typeof event.picker === "object" &&
      Array.isArray(event.picker.teams) &&
      event.picker.teams.length
    ) {
      kpiTeamPickerAcc = event.picker;
      invokeStreamCallback(callbacks.onKpiTeamPicker, event.picker);
    } else if (
      event.type === "chart" &&
      event.chart &&
      typeof event.chart === "object"
    ) {
      chartsAcc = [...chartsAcc, event.chart];
      invokeStreamCallback(callbacks.onChart, event.chart, chartsAcc);
    } else if (event.type === "error") {
      streamError = String(event.message || "LLM error");
      invokeStreamCallback(callbacks.onError, streamError);
    } else if (event.type === "done") {
      donePayload = event;
      if (event.content != null) {
        contentAcc = String(event.content);
      }
      const donePicker = event.pivony_dashboard_picker;
      if (
        donePicker &&
        typeof donePicker === "object" &&
        Array.isArray(donePicker.dashboards) &&
        donePicker.dashboards.length
      ) {
        dashboardPickerAcc = donePicker;
      }
      const donePeriodPicker = event.pivony_period_picker;
      if (
        donePeriodPicker &&
        typeof donePeriodPicker === "object" &&
        Array.isArray(donePeriodPicker.periods) &&
        donePeriodPicker.periods.length
      ) {
        periodPickerAcc = donePeriodPicker;
      }
      const doneKpiMetricPicker = event.pivony_kpi_metric_picker;
      if (
        doneKpiMetricPicker &&
        typeof doneKpiMetricPicker === "object" &&
        Array.isArray(doneKpiMetricPicker.metrics) &&
        doneKpiMetricPicker.metrics.length
      ) {
        kpiMetricPickerAcc = doneKpiMetricPicker;
      }
      const doneKpiTeamPicker = event.pivony_kpi_team_picker;
      if (
        doneKpiTeamPicker &&
        typeof doneKpiTeamPicker === "object" &&
        Array.isArray(doneKpiTeamPicker.teams) &&
        doneKpiTeamPicker.teams.length
      ) {
        kpiTeamPickerAcc = doneKpiTeamPicker;
      }
      if (Array.isArray(event.pivony_charts)) {
        chartsAcc = event.pivony_charts.filter(
          (item) => item && typeof item === "object",
        );
      }
      invokeStreamCallback(callbacks.onDone, event);
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split(/\r?\n\r?\n/);
    buffer = parts.pop() || "";
    parts.forEach((block) => {
      block.split("\n").forEach(parseEvent);
    });
  }
  if (buffer.trim()) {
    buffer.split("\n").forEach(parseEvent);
  }

  if (donePayload) {
    return {
      content: contentAcc,
      suggested_followups: Array.isArray(donePayload.pivony_suggested_followups)
        ? donePayload.pivony_suggested_followups
        : [],
      guidance:
        typeof donePayload.pivony_guidance === "string"
          ? donePayload.pivony_guidance
          : "",
      dashboard_picker: dashboardPickerAcc || undefined,
      period_picker: periodPickerAcc || undefined,
      kpi_metric_picker: kpiMetricPickerAcc || undefined,
      kpi_team_picker: kpiTeamPickerAcc || undefined,
      charts: chartsAcc,
    };
  }

  if (streamError) {
    return { error: streamError };
  }

  return { error: "Malformed response from LLM service" };
};

/**
 * POST dashboards/cx_gpt/chat — multi-turn Pivony Advisor (Ollama / BYOK via API).
 * Uses getFullResult so API `{ error: "..." }` bodies are returned (not `false`).
 *
 * @param {Array<{ role: 'user' | 'assistant', content: string }>} messages
 * @param {{ context?: object, model?: string }} [options] — context for system prompt; optional model id
 * @returns {Promise<{ success?: string, suggested_followups?: string[], guidance?: string, error?: string, cancel?: boolean }>}
 */
export const requestCxGptChat = (messages, options = {}) => {
  const body = { messages };
  const context = options.context && typeof options.context === "object" ? options.context : {};
  body.context = { ...context, localDate: localDay() };
  if (options.model != null && String(options.model).trim()) {
    body.model = String(options.model).trim();
  }
  if (options.provider != null && String(options.provider).trim()) {
    body.provider = String(options.provider).trim().toLowerCase();
  }
  if (options.sessionId != null && String(options.sessionId).trim()) {
    body.session_id = String(options.sessionId).trim();
  }
  if (options.contextUsedTokens != null) {
    body.context_used_tokens = Number(options.contextUsedTokens);
  }
  if (options.contextMaxTokens != null) {
    body.context_max_tokens = Number(options.contextMaxTokens);
  }
  if (options.lastRoute != null && String(options.lastRoute).trim()) {
    body.last_route = String(options.lastRoute).trim();
  }
  return apiRequest("dashboards/cx_gpt/chat", "POST", body, true);
};

/** Escape text for safe HTML (code blocks). */
export const escapeHtml = (s) => {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

/**
 * Split response into alternating text and code segments (``` fenced blocks).
 */
const segmentByCodeFences = (response) => {
  const segments = [];
  const re = /```([\w]*)\n?([\s\S]*?)```/g;
  let last = 0;
  let m = re.exec(response);
  while (m !== null) {
    if (m.index > last) {
      segments.push({ type: "text", content: response.slice(last, m.index) });
    }
    segments.push({ type: "code", lang: m[1] || "", code: m[2].trim() });
    last = re.lastIndex;
    m = re.exec(response);
  }
  if (last < response.length) {
    segments.push({ type: "text", content: response.slice(last) });
  }
  return segments.length ? segments : [{ type: "text", content: response }];
};

const formatTextChunk = (chunk) => {
  if (!chunk) return "";
  return chunk
    .replace(/#\s*(.+)/g, "<h3>$1</h3>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(?:^|\n)\*\s(.+)/g, "<li>$1</li>")
    .replace(/(<li>.*<\/li>)(?!<\/ul>)/g, "<ul>$1</ul>")
    .replace(/\n/g, "<br/>");
};

/**
 * Light markdown-style formatting for assistant HTML (aligned with ChatBot).
 * Code fences become copy-friendly blocks (see .cxGptCodeBlock / .cxGptCodeCopy in panel).
 */
export const formatGptResponseHtml = (response) => {
  if (!response || typeof response !== "string") return "";
  const parts = segmentByCodeFences(response);
  return parts
    .map((seg) => {
      if (seg.type === "code") {
        const safe = escapeHtml(seg.code);
        const lang = seg.lang ? `<span class="cxGptCodeLang">${escapeHtml(seg.lang)}</span>` : "";
        return `<div class="cxGptCodeBlock">${lang}<button type="button" class="cxGptCodeCopy">Copy</button><pre><code>${safe}</code></pre></div>`;
      }
      return formatTextChunk(seg.content);
    })
    .join("");
};
